import { gql } from "graphql-request";

import type { ItemsQuery } from "@mondaydotcomorg/api";
import { OPERATION_COLUMNS } from "@onesolution/constants";
import { QUOTES_COLUMNS } from "@onesolution/constants/monday/quotes";
import type { Context } from "@onesolution/middlewares";
import {
	ALL_MONDAY_SCHEMAS,
	MONDAY_PAGINATED_PARAMS_DEF,
	MONDAY_PAGINATED_PARAMS_USE,
	MONDAY_SCHEMAS,
	type MONDAY_TYPES,
} from "@onesolution/monday";
import {
	type BasicItemWithColTitles,
	basicFormatResult,
} from "@onesolution/serverFn/basicFormatter";
import type { DefaultPaginatedQueryParams } from "../defaultPaginatedParams";
import { formatPaginatedResult } from "../format";
import { requestAndFormat } from "../utils";
import type { OperationItem } from "./operationDetails.graphql";

type BOARD_RELATION<T = object> = {
	display_value: string;
	linked_item_ids: string[];
	linked_items: Array<MONDAY_TYPES["base"] & MONDAY_TYPES["assets"] & T>;
};

type OpOut = {
	cursor: string;
	items: OperationList;
};

type OperationList = Array<
	OperationItem &
		MONDAY_TYPES["assets"] & {
			services: BOARD_RELATION;
			assignees: BOARD_RELATION;
			marketingPlans: BOARD_RELATION;
			campaigns: BOARD_RELATION;
			thumbnail: MONDAY_TYPES["file"];
			productsStatus: MONDAY_TYPES["text"];
			quotes: {
				status: MONDAY_TYPES["status"];
				quoteLink: MONDAY_TYPES["text"];
			}[];
			updates: {
				body: string;
				creator: { name: string };
			}[];
		}
>;

export const operationsPageQuery = (
	context: Context,
	params: DefaultPaginatedQueryParams & { queryParams: ItemsQuery },
) =>
	requestAndFormat(
		{
			defaultParams: {
				boardId: context.boards?.briefs,
				columnIds: Object.values(OPERATION_COLUMNS),
				quotesColumnIds: [QUOTES_COLUMNS.status, QUOTES_COLUMNS.quoteLink],
				queryParams: {
					order_by: [
						{
							column_id: OPERATION_COLUMNS.status,
							direction: "asc",
						},
						{
							column_id: OPERATION_COLUMNS.creationDate,
							direction: "desc",
						},
					],
					operator: "and",
				} as ItemsQuery,
			},
			gql: gql`
                query GetOperationsPageQuery(
					$boardId: ID!,
					$columnIds: [String!],
					$quotesColumnIds: [String!],
					${MONDAY_PAGINATED_PARAMS_DEF(params?.cursor, "$queryParams: ItemsQuery")}
				) {
					
                    boards(ids: [$boardId]) {
                        id
                        items_count
                        name
                        items_page(
							${MONDAY_PAGINATED_PARAMS_USE(params?.cursor, "query_params: $queryParams")}
						) {
                            cursor
                            items {
                                id
                                name
								updates(limit: 500) {
									text_body
									creator {
										name
									}
								}
								
								${MONDAY_SCHEMAS.assets}
                                column_values(ids: $columnIds) {
                                    ${ALL_MONDAY_SCHEMAS}

                                    ... on BoardRelationValue {
                                        display_value
                                        linked_item_ids
                                        linked_items {
                                            id
                                            name
											${MONDAY_SCHEMAS.assets}
											column_values(ids: $quotesColumnIds) {
                                    			${ALL_MONDAY_SCHEMAS}
											}
                                        }
                                    }
                                }
                            }
                        }
                    }
                }`,
			resultKey: "boards[0].items_page.items",
			additionalFormatting: [
				formatPaginatedResult<OperationList[number]>(
					"boards[0].items_page.cursor",
				),
				async (_rawData, formattedData) => {
					const data = formattedData as OpOut;

					return {
						...data,
						items: data.items.map((item) => ({
							...item,
							quotes: basicFormatResult(
								(
									item.quotes as unknown as {
										linked_items: BasicItemWithColTitles[];
									}
								).linked_items,
							),
						})),
					} as unknown as {
						cursor: string;
						items: OperationList;
					};
				},
			],
			outType: {} as OpOut,
		},
		params,
	);
